import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { 
  Mail, 
  Lock, 
  User, 
  Phone, 
  Chrome, 
  Eye, 
  EyeOff, 
  ShoppingBag, 
  ShieldCheck, 
  UserPlus, 
  LogIn, 
  ArrowRight,
  Sparkles,
  Info
} from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';

interface LoginModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  initialMode?: 'login' | 'register';
  noticeMessage?: string;
}

export default function LoginModal({ 
  isOpen: propIsOpen, 
  onClose: propOnClose, 
  initialMode, 
  noticeMessage 
}: LoginModalProps = {}) {
  const { 
    user,
    login, 
    loginWithEmail, 
    registerWithEmail, 
    resetPassword, 
    isLoggingIn,
    isAuthModalOpen,
    authModalMode,
    authModalNotice,
    closeAuthModal
  } = useAuth();

  // Support both controlled props (from Navbar) and global context state
  const isOpen = propIsOpen !== undefined ? propIsOpen : isAuthModalOpen;
  const handleClose = () => {
    if (propOnClose) propOnClose();
    closeAuthModal();
  };

  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  // Sync mode when modal opens
  useEffect(() => {
    if (isOpen) {
      if (initialMode) {
        setMode(initialMode);
      } else if (authModalMode) {
        setMode(authModalMode);
      }
      setIsForgotPassword(false);
    }
  }, [isOpen, initialMode, authModalMode]);

  // Auto-close if user is already authenticated
  useEffect(() => {
    if (user && isOpen) {
      handleClose();
    }
  }, [user, isOpen]);

  const activeNotice = noticeMessage || authModalNotice;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isForgotPassword) {
        if (!formData.email) {
          toast.error("Silakan masukkan alamat email Anda.");
          return;
        }
        await resetPassword(formData.email);
        setIsForgotPassword(false);
      } else if (mode === 'register') {
        if (!formData.name.trim()) {
          toast.error("Harap masukkan nama lengkap Anda.");
          return;
        }
        const cleanPhone = formData.phone.replace(/[^0-9]/g, '');
        if (!cleanPhone || cleanPhone.length < 9) {
          toast.error("Harap masukkan nomor WhatsApp / HP aktif yang valid (minimal 9 digit).");
          return;
        }
        if (!formData.email) {
          toast.error("Harap masukkan alamat email.");
          return;
        }
        if (formData.password.length < 6) {
          toast.error("Kata sandi minimal 6 karakter.");
          return;
        }
        if (formData.password !== formData.confirmPassword) {
          toast.error("Konfirmasi kata sandi tidak cocok. Silakan periksa kembali.");
          return;
        }

        await registerWithEmail(formData.email, formData.password, formData.name.trim(), formData.phone);
        toast.success(`Selamat datang, ${formData.name.trim()}! Akun Anda berhasil terdaftar.`);
        handleClose();
      } else {
        if (!formData.email || !formData.password) {
          toast.error("Silakan isi email dan kata sandi Anda.");
          return;
        }
        await loginWithEmail(formData.email, formData.password);
        toast.success("Berhasil masuk ke akun Anda!");
        handleClose();
      }
    } catch (error) {
      // Handled in AuthContext
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await login();
      handleClose();
    } catch (error) {
      // Handled in AuthContext
    }
  };

  if (!isOpen) return null;

  if (isForgotPassword) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md bg-card border-border text-foreground rounded-3xl overflow-hidden p-0 shadow-2xl">
          <div className="p-6 sm:p-8 space-y-6">
            <DialogHeader className="text-center">
              <div className="mx-auto w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-3">
                <Lock className="w-6 h-6" />
              </div>
              <DialogTitle className="text-2xl sm:text-3xl font-bold text-foreground">
                Lupa Kata Sandi
              </DialogTitle>
              <DialogDescription className="text-foreground/60 text-sm">
                Masukkan alamat email yang terdaftar. Kami akan mengirimkan tautan untuk mengatur ulang kata sandi Anda.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email" className="text-sm font-semibold">Email Terdaftar</Label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
                  <Input 
                    id="reset-email"
                    type="email"
                    required
                    disabled={isLoggingIn}
                    placeholder="nama@email.com"
                    className="bg-muted/50 border-border pl-10 h-12 rounded-xl focus:border-primary text-foreground text-sm"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={isLoggingIn}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold h-12 rounded-xl shadow-lg shadow-primary/20 transition-all text-sm"
              >
                {isLoggingIn ? 'Mengirim Tautan...' : 'Kirim Tautan Reset Sandi'}
              </Button>
            </form>

            <div className="text-center pt-2 border-t border-border">
              <button 
                type="button"
                onClick={() => setIsForgotPassword(false)}
                disabled={isLoggingIn}
                className="text-sm text-primary font-bold hover:underline disabled:opacity-50"
              >
                ← Kembali ke Halaman Masuk
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md md:max-w-lg bg-card border-border text-foreground rounded-3xl overflow-hidden p-0 shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="p-6 sm:p-8 space-y-5">
          
          {/* Top Switcher Tabs */}
          <div className="flex bg-muted/70 p-1.5 rounded-2xl border border-border">
            <button
              type="button"
              onClick={() => setMode('login')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                mode === 'login'
                  ? 'bg-card text-primary shadow-sm'
                  : 'text-foreground/60 hover:text-foreground'
              }`}
            >
              <LogIn className="w-4 h-4" /> Masuk Akun
            </button>
            <button
              type="button"
              onClick={() => setMode('register')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                mode === 'register'
                  ? 'bg-card text-primary shadow-sm'
                  : 'text-foreground/60 hover:text-foreground'
              }`}
            >
              <UserPlus className="w-4 h-4" /> Daftar Akun Baru
            </button>
          </div>

          {/* Contextual Notice (e.g. from Beli Sekarang) */}
          {activeNotice ? (
            <div className="p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-foreground flex items-start gap-3">
              <div className="p-2 rounded-xl bg-amber-500/20 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5">
                <ShoppingBag className="w-4 h-4" />
              </div>
              <div className="text-xs leading-relaxed">
                <p className="font-bold text-amber-700 dark:text-amber-400 mb-0.5">
                  Wajib Masuk / Daftar Akun Terlebih Dahulu
                </p>
                <p className="text-foreground/80">
                  {activeNotice}
                </p>
              </div>
            </div>
          ) : (
            <DialogHeader className="text-center pt-1">
              <DialogTitle className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight">
                {mode === 'register' ? 'Daftar Akun Baru' : 'Selamat Datang Kembali'}
              </DialogTitle>
              <DialogDescription className="text-foreground/60 text-xs sm:text-sm">
                {mode === 'register' 
                  ? 'Lengkapi data diri Anda untuk mendaftar akun dan mengakses seluruh produk & layanan.' 
                  : 'Masuk untuk mengakses dashboard, riwayat pesanan, dan file unduhan Anda.'}
              </DialogDescription>
            </DialogHeader>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3.5">
            {mode === 'register' && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="auth-name" className="text-xs sm:text-sm font-semibold">
                    Nama Lengkap <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
                    <Input 
                      id="auth-name"
                      required
                      disabled={isLoggingIn}
                      placeholder="Contoh: Budi Santoso"
                      className="bg-muted/50 border-border pl-10 h-11 sm:h-12 rounded-xl focus:border-primary text-foreground text-sm"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="auth-phone" className="text-xs sm:text-sm font-semibold">
                    Nomor WhatsApp / HP Aktif <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
                    <Input 
                      id="auth-phone"
                      type="tel"
                      required
                      disabled={isLoggingIn}
                      placeholder="Contoh: 081234567890"
                      className="bg-muted/50 border-border pl-10 h-11 sm:h-12 rounded-xl focus:border-primary text-foreground text-sm"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                  <p className="text-[10px] text-foreground/50">
                    * Nomor WhatsApp aktif untuk konfirmasi pesanan dan pengiriman invoice.
                  </p>
                </div>
              </>
            )}
            
            <div className="space-y-1.5">
              <Label htmlFor="auth-email" className="text-xs sm:text-sm font-semibold">
                Alamat Email <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
                <Input 
                  id="auth-email"
                  type="email"
                  required
                  disabled={isLoggingIn}
                  placeholder="nama@email.com"
                  className="bg-muted/50 border-border pl-10 h-11 sm:h-12 rounded-xl focus:border-primary text-foreground text-sm"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex justify-between items-center">
                <Label htmlFor="auth-password" className="text-xs sm:text-sm font-semibold">
                  Kata Sandi <span className="text-red-500">*</span>
                </Label>
                {mode === 'login' && (
                  <button 
                    type="button"
                    onClick={() => setIsForgotPassword(true)}
                    className="text-xs text-primary font-medium hover:underline"
                  >
                    Lupa Password?
                  </button>
                )}
              </div>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
                <Input 
                  id="auth-password"
                  type={showPassword ? "text" : "password"}
                  required
                  disabled={isLoggingIn}
                  placeholder={mode === 'register' ? 'Minimal 6 karakter' : '••••••••'}
                  className="bg-muted/50 border-border pl-10 pr-12 h-11 sm:h-12 rounded-xl focus:border-primary text-foreground text-sm"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center h-8 w-8 text-foreground/50 hover:text-foreground transition-colors z-20 focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {mode === 'register' && (
              <div className="space-y-1.5">
                <Label htmlFor="auth-confirm-password" className="text-xs sm:text-sm font-semibold">
                  Konfirmasi Kata Sandi <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <ShieldCheck className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
                  <Input 
                    id="auth-confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    disabled={isLoggingIn}
                    placeholder="Ulangi kata sandi Anda"
                    className="bg-muted/50 border-border pl-10 pr-12 h-11 sm:h-12 rounded-xl focus:border-primary text-foreground text-sm"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center h-8 w-8 text-foreground/50 hover:text-foreground transition-colors z-20 focus:outline-none"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            )}

            <Button 
              type="submit" 
              disabled={isLoggingIn}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold h-12 rounded-xl shadow-lg shadow-primary/20 transition-all text-sm mt-2"
            >
              {isLoggingIn ? (
                'Memproses Akun...'
              ) : mode === 'register' ? (
                <span className="flex items-center gap-1.5">
                  Daftar Akun Sekarang <ArrowRight className="w-4 h-4" />
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  Masuk Sekarang <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          {/* Social Divider */}
          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center">
              <Separator className="bg-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-3 text-foreground/40 font-medium tracking-wider text-[11px]">
                Atau {mode === 'register' ? 'Daftar' : 'Masuk'} dengan Google
              </span>
            </div>
          </div>

          {/* Google Button */}
          <Button 
            type="button"
            variant="outline" 
            onClick={handleGoogleLogin}
            disabled={isLoggingIn}
            className="w-full border-border bg-muted/40 hover:bg-muted text-foreground font-semibold h-12 rounded-xl transition-all text-xs sm:text-sm flex items-center justify-center gap-2.5"
          >
            <Chrome className="h-4 w-4 text-red-500" />
            <span>Lanjutkan dengan Akun Google</span>
          </Button>

          {/* Bottom Switcher Footer */}
          <div className="text-center pt-2 border-t border-border">
            <p className="text-xs sm:text-sm text-foreground/60">
              {mode === 'register' ? 'Sudah memiliki akun?' : 'Belum memiliki akun?'}
              <button 
                type="button"
                onClick={() => setMode(mode === 'register' ? 'login' : 'register')}
                disabled={isLoggingIn}
                className="ml-1.5 text-primary font-bold hover:underline disabled:opacity-50 inline-flex items-center gap-0.5"
              >
                {mode === 'register' ? 'Masuk di sini' : 'Daftar akun baru'}
              </button>
            </p>
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
}
